import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Col, Container } from "reactstrap";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import NGITable from "components/Common/NGITable";
import { FILTER_PAGE, USER_ROLES } from "utils/constants";
import useStoreFilters from "customHooks/useStoreFilters";
import { isEmpty } from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import classname from "classnames";
import { customerBlacklistListColumns } from "pages/price-management/CustomerBlacklist/customerBlacklistListColumns";
import CustomerBlacklistFilter from "pages/price-management/CustomerBlacklist/customerBlacklistFilter";
import { removeCustomerBlacklist, retrieveCustomerBlacklist } from "utils/api";
import { getDictionary } from "store/dictionaries/actions";
import { DICTIONARY_TYPES } from "store/dictionaries/constants";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "components/Common/buttons/CustomButton";
import NGIDropdownButton from "components/Common/buttons/NGIDropdownButton";
import AddCustomerModal from "pages/price-management/CustomerBlacklist/AddCustomerModal";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { DictionariesSelector } from "store/dictionaries/selectors";
import { ADD_REMOVE_TYPES } from "pages/price-management/CustomerBlacklist/variables";
import AddBulkCustomerModal from "pages/price-management/CustomerBlacklist/AddBulkCustomerModal";

const useStyles = makeStyles({
  filters: { display: "flex", justifyContent: "space-between" },
  overflowText: {
    overflow: "hidden",
    maxWidth: 110,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
});

const CustomerBlacklist = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isLoading, setLoader] = useState(false);
  const [openAddCustomerModal, setAddCustomerModal] = useState(false);
  const [openAddBulkCustomerModal, setAddBulkCustomerModal] = useState(null);
  const [removeConfirmationId, setRemoveConfirmationId] = useState(null);
  const { filters, setFilters } = useStoreFilters(FILTER_PAGE.CUSTOMER_BLACKLIST);
  const [list, setList] = useState([]);

  const {
    list: { blacklistRemovingReason },
  } = useSelector(DictionariesSelector);

  useEffect(() => {
    dispatch(getDictionary(DICTIONARY_TYPES.BLACKLIST_ADDING_REASON));
    dispatch(getDictionary(DICTIONARY_TYPES.BLACKLIST_REMOVING_REASON));
    dispatch(getDictionary(DICTIONARY_TYPES.DEPOT));
    handleFilter(!isEmpty(filters) ? filters : {});
  }, []);

  const handleFilter = useCallback((options = {}) => {
    setFilters(options);
    setLoader(true);
    retrieveCustomerBlacklist(options)
      .then(res => {
        setList(res?.data || null);
        setLoader(false);
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, []);

  const handleRemove = useCallback(
    (comment, reason) => {
      setLoader(true);
      removeCustomerBlacklist({
        customerId: removeConfirmationId,
        removeReason: reason,
        ...(comment ? { comments: comment } : {}),
      })
        .then(() => {
          successMessage("Customer was removed from the blacklist successfully");
          setLoader(false);
          setTimeout(() => handleFilter(), 1500);
          removeModalClose();
        })
        .catch(e => {
          setLoader(false);
          defaultNgiErrorMethod(e);
        });
    },
    [removeConfirmationId]
  );

  const addCustomerModalOpen = useCallback(() => setAddCustomerModal(true), []);
  const addCustomerModalClose = useCallback(() => setAddCustomerModal(false), []);

  const removeModalOpen = useCallback(customer => setRemoveConfirmationId(customer.customerId), []);
  const removeModalClose = useCallback(() => setRemoveConfirmationId(null), []);

  const addBulkCustomerModalOpen = useCallback(type => setAddBulkCustomerModal(type), []);
  const addBulkCustomerModalClose = useCallback(() => setAddBulkCustomerModal(null), []);

  const columns = useMemo(() => customerBlacklistListColumns(removeModalOpen, classes), []);

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <div className={classname(classes.filters, "filters-wrapper")}>
            <div className="filter-buttons">
              <NGIDropdownButton
                roles={[USER_ROLES.IT_OPERATIONS, USER_ROLES.IT_OPERATIONS_LEADERSHIP, USER_ROLES.FINANCE]}
                toggleEl={<CustomButton isDropdown type="primary" text="Blacklist Customers" />}
                menuItems={[
                  {
                    label: "Add customer to blacklist",
                    callback: addCustomerModalOpen,
                  },
                  {
                    label: "Bulk add to blacklist",
                    callback: () => addBulkCustomerModalOpen(ADD_REMOVE_TYPES.ADD),
                  },
                  {
                    label: "Bulk remove from blacklist",
                    callback: () => addBulkCustomerModalOpen(ADD_REMOVE_TYPES.REMOVE),
                  },
                ]}
              />
            </div>
            <CustomerBlacklistFilter onFilter={handleFilter} isLoading={isLoading} />
          </div>
          <Col>
            <NGITable
              filtersType={FILTER_PAGE.CUSTOMER_BLACKLIST}
              columns={columns}
              data={list}
              isLoading={isLoading}
              updateList={handleFilter}
            />
          </Col>
        </Container>
        {openAddCustomerModal && <AddCustomerModal update={handleFilter} onClose={addCustomerModalClose} />}
        {openAddBulkCustomerModal && (
          <AddBulkCustomerModal
            type={openAddBulkCustomerModal}
            update={handleFilter}
            onClose={addBulkCustomerModalClose}
          />
        )}
        {removeConfirmationId && (
          <ConfirmationModal
            title="Confirmation Removal"
            description="Are you sure you want to do remove this customer from blacklist?"
            reasons={blacklistRemovingReason}
            onConfirm={handleRemove}
            cancelBtnTitle="Cancel"
            confirmBtnTitle="Confirm"
            onCancel={removeModalClose}
            reasonTitle="Reason for Removing*"
            commentTitle="Additional Comment"
            isReasonRequired
            withComment
          />
        )}
      </div>
    </>
  );
};

export default CustomerBlacklist;
